import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import AppShell from './app/layout/AppShell';
import LoginPage from './features/auth/LoginPage';
import ActivatePage from './features/auth/ActivatePage';
import DashboardPage from './features/dashboard/DashboardPage';
import CompaniesPage from './features/systemManagement/companies/CompaniesPage';
import PeriodsPage from './features/systemManagement/periods/PeriodsPage';
import PermissionsPage from './features/systemManagement/permissions/PermissionsPage';
import RatioDefinitionsPage from './features/systemManagement/ratios/RatioDefinitionsPage';
import SectorRatiosPage from './features/systemManagement/sectorRatios/SectorRatiosPage';
import RegionsPage from './features/systemManagement/regions/RegionsPage';
import RolesPage from './features/systemManagement/roles/RolesPage';
import SectorGroupsPage from './features/systemManagement/sectorGroups/SectorGroupsPage';
import SectorsPage from './features/systemManagement/sectors/SectorsPage';
import UsersPage from './features/systemManagement/users/UsersPage';
import StandardRatioDefinitionsPage from './features/systemManagement/standardRatios/StandardRatioDefinitionsPage';
import NotFoundPage from './shared/components/NotFoundPage';
import RequireAuth from './features/auth/RequireAuth';
import RequireRole from './features/auth/RequireRole';
import TDHPPage from './features/dataEntry/tdhp/TDHPPage';
import AccountingVouchersImportPage from './features/dataEntry/accountingVouchersImport/AccountingVouchersImportPage';
import MizanImportPage from './features/dataEntry/mizanImport/MizanImportPage';
import GeneralLedgerTrialBalancePage from './features/financialStatements/generalLedgerTrialBalance/GeneralLedgerTrialBalancePage';
import IncomeStatementPage from './features/financialStatements/incomeStatement/IncomeStatementPage';
import BalanceSheetPage from './features/financialStatements/balanceSheet/BalanceSheetPage';
import AccountStatementPage from './features/reports/accountStatement/AccountStatementPage';
import MonthlyIncomeExpenseReportPage from './features/reports/monthlyIncomeExpenseReport/MonthlyIncomeExpenseReportPage';
import StandardRatiosPage from './features/analysis/ratiosStandard/StandardRatiosPage';
import RatioTrendAnalysisPage from './features/analysis/ratioTrend/RatioTrendAnalysisPage';
import SectorRatioAnalysisPage from './features/analysis/sectorRatio/SectorRatioAnalysisPage';
import AiFinancialReportPage from './features/analysis/aiReport/AiFinancialReportPage';
import FinancialAnalysisDashboardPage from './features/analysis/dashboard/FinancialAnalysisDashboardPage';
import StressTestPage from './features/analysis/stressTest/StressTestPage';
import DraftTrialBalancePage from './features/transferredData/draftTrialBalance/DraftTrialBalancePage';
import AccountingVouchersPage from './features/transferredData/accountingVouchers/AccountingVouchersPage';
import CfoFxPage from './features/cfoPanel/fx/CfoFxPage';
import CfoGoldPage from './features/cfoPanel/gold/CfoGoldPage';
import CfoInterestPage from './features/cfoPanel/interest/CfoInterestPage';
import CfoTufePage from './features/cfoPanel/priceIndices/CfoTufePage';
import CfoCapacityPage from './features/cfoPanel/capacity/CfoCapacityPage';
import CfoSectorBalancePage from './features/cfoPanel/sektor-bilancolari/MacroDataPage';
import HomePage from './features/home/HomePage';
import ReverseBalancePage from './features/financialAnalysis/reverseBalance/ReverseBalancePage';
import PnlClosingPage from './features/financialAnalysis/pnlClosing/PnlClosingPage';
import BalanceClosingPage from './features/financialAnalysis/balanceClosing/BalanceClosingPage';
import AiUsageCostsPage from './features/systemManagement/aiUsageCosts/AiUsageCostsPage';
import IfrsStandardsAdminPage from './features/systemManagement/ifrsStandards/IfrsStandardsAdminPage';
import IfrsWorkspacePage from './features/ifrs/IfrsWorkspacePage';
import IfrsReportModulePage from './features/ifrs/IfrsReportModulePage';
import VukIfrsTransformationWizardPage from './features/ifrs/VukIfrsTransformationWizardPage';
import IfrsWorksheetPage from './features/ifrs/IfrsWorksheetPage';
import { LanguageProvider } from './app/i18n/LanguageContext';

const routerBaseName = (process.env.REACT_APP_BASENAME || '').trim() || undefined;

function App() {
  return (
    <div className="app-root">
      <LanguageProvider>
        <BrowserRouter basename={routerBaseName}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/activate/:token" element={<ActivatePage />} />
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/home" element={<HomePage />} />
            <Route
              path="/users"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN', 'TENANT_ADMIN']}>
                  <UsersPage />
                </RequireRole>
              )}
            />
            <Route
              path="/roles"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN']}>
                  <RolesPage />
                </RequireRole>
              )}
            />
            <Route
              path="/permissions"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN']}>
                  <PermissionsPage />
                </RequireRole>
              )}
            />
            <Route
              path="/regions"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN']}>
                  <RegionsPage />
                </RequireRole>
              )}
            />
            <Route
              path="/sector-groups"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN']}>
                  <SectorGroupsPage />
                </RequireRole>
              )}
            />
            <Route
              path="/sectors"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN']}>
                  <SectorsPage />
                </RequireRole>
              )}
            />
            <Route
              path="/system/ratios"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN']}>
                  <RatioDefinitionsPage />
                </RequireRole>
              )}
            />
            <Route
              path="/sector-ratios"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN']}>
                  <SectorRatiosPage />
                </RequireRole>
              )}
            />
            <Route
              path="/companies"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN', 'TENANT_ADMIN']}>
                  <CompaniesPage />
                </RequireRole>
              )}
            />
            <Route
              path="/periods"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN', 'TENANT_ADMIN']}>
                  <PeriodsPage />
                </RequireRole>
              )}
            />
            <Route
              path="/system/ai-usage-costs"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN']}>
                  <AiUsageCostsPage />
                </RequireRole>
              )}
            />
            <Route
              path="/system/standard-ratios"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN']}>
                  <StandardRatioDefinitionsPage />
                </RequireRole>
              )}
            />
            <Route
              path="/system/ifrs-standards"
              element={(
                <RequireRole allowedRoles={['SYS_ADMIN']}>
                  <IfrsStandardsAdminPage />
                </RequireRole>
              )}
            />
            <Route
              path="/data-ingestion/tdhp-plan"
              element={<TDHPPage />}
            />
            <Route path="/data-ingestion/mizan-import" element={<MizanImportPage />} />
            <Route path="/data-ingestion/fis-import" element={<AccountingVouchersImportPage />} />
            <Route path="/tdhp-plan" element={<Navigate to="/data-ingestion/tdhp-plan" replace />} />
            <Route path="/mizan-import" element={<Navigate to="/data-ingestion/mizan-import" replace />} />
            <Route path="/fis-import" element={<Navigate to="/data-ingestion/fis-import" replace />} />
            <Route path="/transferred-data/draft-trial-balance" element={<DraftTrialBalancePage />} />
            <Route path="/transferred-data/accounting-vouchers" element={<AccountingVouchersPage />} />
            <Route
              path="/financial-statements/general-ledger-trial-balance"
              element={<GeneralLedgerTrialBalancePage />}
            />
            <Route path="/financial-statements/income-statement" element={<IncomeStatementPage />} />
            <Route path="/financial-statements/balance-sheet" element={<BalanceSheetPage />} />
            <Route path="/financial-statements/ifrs-worksheet" element={<IfrsWorksheetPage />} />
            <Route path="/financial-statements/ifrs-workspace" element={<IfrsWorkspacePage />} />
            <Route path="/financial-statements/vuk-ifrs-wizard" element={<VukIfrsTransformationWizardPage />} />
            <Route path="/financial-statements/ifrs-reports/financial-position" element={<IfrsReportModulePage reportType="financialPosition" />} />
            <Route path="/financial-statements/ifrs-reports/profit-loss-oci" element={<IfrsReportModulePage reportType="profitLossOci" />} />
            <Route path="/financial-statements/ifrs-reports/cash-flow" element={<IfrsReportModulePage reportType="cashFlow" />} />
            <Route path="/financial-statements/ifrs-reports/equity-changes" element={<IfrsReportModulePage reportType="equityChanges" />} />
            <Route path="/financial-statements/ifrs-reports/notes" element={<IfrsReportModulePage reportType="notes" />} />
            <Route path="/financial-statements/ifrs-reports/vuk-ifrs-comparison" element={<IfrsReportModulePage reportType="vukIfrsComparison" />} />
            <Route path="/financial-analysis/reverse-balance" element={<ReverseBalancePage />} />
            <Route path="/financial-analysis/pnl-closing" element={<PnlClosingPage />} />
            <Route path="/financial-analysis/balance-closing" element={<BalanceClosingPage />} />
            <Route path="/reports/account-statement" element={<AccountStatementPage />} />
            <Route path="/reports/monthly-income-expense" element={<MonthlyIncomeExpenseReportPage />} />
            <Route path="/analysis/ratios-standard" element={<StandardRatiosPage />} />
            <Route path="/analysis/ratio-trend" element={<RatioTrendAnalysisPage />} />
            <Route path="/analysis/sector-ratio" element={<SectorRatioAnalysisPage />} />
            <Route path="/analysis/ai-report" element={<AiFinancialReportPage />} />
            <Route path="/analysis/dashboard" element={<FinancialAnalysisDashboardPage />} />
            <Route path="/analysis/stress-test" element={<StressTestPage />} />
            <Route path="/makro-veriler/doviz" element={<CfoFxPage />} />
            <Route path="/makro-veriler/altin" element={<CfoGoldPage />} />
            <Route path="/makro-veriler/faiz" element={<CfoInterestPage />} />
            <Route path="/makro-veriler/tufe" element={<CfoTufePage />} />
            <Route
              path="/makro-veriler/kapasite-kullanim"
              element={<CfoCapacityPage />}
            />
            <Route
              path="/makro-veriler/sektor-bilancolari"
              element={<CfoSectorBalancePage />}
            />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;

